// C:\Users\avnis\CareCo-OrdinatorDashboard\client\src\CareCoordinator\pages\carecoordinatorlist.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listAllCareCoordinators, extractArray } from "../data/admindata";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f6fb",
    padding: "32px 24px",
    fontFamily: "'Segoe UI', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 28,
  },
  backBtn: {
    background: "none",
    border: "1.5px solid #c0c8e0",
    borderRadius: 8,
    padding: "6px 16px",
    cursor: "pointer",
    fontSize: 14,
    color: "#4a5568",
    fontWeight: 500,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a2340",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: "#8492a6",
    marginLeft: "auto",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 20,
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 12px rgba(60,80,140,0.07)",
    padding: "22px 24px",
    cursor: "pointer",
    border: "1.5px solid transparent",
    transition: "box-shadow 0.18s, border-color 0.18s, transform 0.15s",
    position: "relative",
  },
  cardHovered: {
    boxShadow: "0 6px 24px rgba(60,80,140,0.14)",
    borderColor: "#6c8ef5",
    transform: "translateY(-2px)",
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#6c8ef5,#4a6cf7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: 18,
    marginBottom: 14,
  },
  cardName: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1a2340",
    marginBottom: 6,
  },
  cardMeta: {
    fontSize: 13,
    color: "#6b7a99",
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  badge: {
    display: "inline-block",
    background: "#eef1fd",
    color: "#4a6cf7",
    borderRadius: 20,
    padding: "3px 12px",
    fontSize: 12,
    fontWeight: 600,
    marginTop: 10,
  },
  arrow: {
    position: "absolute",
    top: 20,
    right: 20,
    fontSize: 22,
    color: "#b0bcd4",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 0",
    color: "#8492a6",
    fontSize: 15,
  },
  spinner: {
    textAlign: "center",
    padding: "80px 0",
    color: "#8492a6",
    fontSize: 16,
  },
};

// ─── Single coordinator card ──────────────────────────────────────────────────
function CoordinatorCard({ coordinator, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{ ...styles.card, ...(hovered ? styles.cardHovered : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <div style={styles.arrow}>›</div>

      <div style={styles.avatar}>
        {coordinator.name?.charAt(0)?.toUpperCase() || "?"}
      </div>

      <div style={styles.cardName}>{coordinator.name || "—"}</div>

      <div style={styles.cardMeta}>
        <span>✉</span> {coordinator.email || "—"}
      </div>
      <div style={styles.cardMeta}>
        <span>📞</span> {coordinator.phone_no || "—"}
      </div>

      <span style={styles.badge}>
        {coordinator.patient_count ?? 0} patient{coordinator.patient_count !== 1 ? "s" : ""}
      </span>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CareCoordinatorList() {
  const navigate = useNavigate();
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const raw = await listAllCareCoordinators();
        setCoordinators(extractArray(raw));
      } catch (err) {
        setError(err.message || "Failed to load care coordinators");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Clicking a card navigates to ProgramOverview, carrying admin context in state
  const handleCardClick = (cc) => {
    navigate("/care-coordinator/program", {
      state: {
        isAdmin: true,
        careCoordinatorId: cc.care_coordinator_id,
        careCoordinatorName: cc.name,
      },
    });
  };

  if (loading) return <div style={styles.page}><div style={styles.spinner}>Loading care coordinators…</div></div>;
  if (error)   return <div style={styles.page}><div style={{ ...styles.emptyState, color: "#e55353" }}>{error}</div></div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/caretaker-login")}>← Back to Login</button>
        <h1 style={styles.title}>Care Coordinators</h1>
        <span style={styles.subtitle}>
          {coordinators.length} coordinator{coordinators.length !== 1 ? "s" : ""} found
        </span>
      </div>

      {coordinators.length === 0 ? (
        <div style={styles.emptyState}>No care coordinators found.</div>
      ) : (
        <div style={styles.grid}>
          {coordinators.map((cc) => (
            <CoordinatorCard
              key={cc.care_coordinator_id}
              coordinator={cc}
              onClick={() => handleCardClick(cc)}
            />
          ))}
        </div>
      )}
    </div>
  );
}